/**
 * Studio Admin → Cowork. Every session in every organization, and everything people wanted and did
 * not get: the "What we don't have" lines of their reports, follow-ups that went unanswered, "Changed
 * it first" and "Didn't use it" clicks, and what they typed under a report. Platform admins only.
 * Opening this page and reading a session are recorded.
 */
import { RotateCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  adminClient,
  type CoworkMiss,
  type CoworkMissesResponse,
  type CoworkMissStatus,
  type CoworkSessionRow,
} from '@/api/adminClient';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminSection } from '@/components/shell/AdminSection';
import { DataTable, DataTableCell, DataTableHead, DataTableRow } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import { LoadError, SkeletonList } from '@/components/ui/load-state';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';

import { coworkSessionPath, KIND_LABELS, ORIGIN_LABELS, STATUSES } from './coworkModel';

const FIELD_CLASS = 'h-9 rounded-lg border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function MissRow({ miss, organizationName, onStatus }: { miss: CoworkMiss; organizationName: string; onStatus: (status: CoworkMissStatus) => void }) {
  return (
    <li className="flex flex-col gap-1 border-t border-border py-3 first:border-t-0 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <div className="min-w-0">
        <p className="text-sm text-foreground">{miss.text}</p>
        {miss.meant ? <p className="text-sm text-muted-foreground">Read as: {miss.meant}</p> : null}
        <p className="mt-1 text-xs text-muted-foreground">
          {ORIGIN_LABELS[miss.origin]} · {miss.owner} · {organizationName} · {formatDateTime(miss.createdAt)}
          {miss.artifactVersion ? ` · v${miss.artifactVersion}` : ''}
        </p>
        <Link className="text-xs text-muted-foreground underline" to={coworkSessionPath(miss.sessionId)}>
          {miss.question || 'Open session'}
        </Link>
      </div>
      <select aria-label="Status" className={FIELD_CLASS} value={miss.status} onChange={(event) => onStatus(event.target.value as CoworkMissStatus)}>
        {STATUSES.map((status) => (
          <option key={status} value={status}>{status}</option>
        ))}
      </select>
    </li>
  );
}

function Sessions({ sessions, more, onMore }: { sessions: CoworkSessionRow[]; more: boolean; onMore: () => void }) {
  return (
    <DataTable footer={more ? <div className="border-t border-border px-4 py-3"><Button type="button" variant="outline" size="sm" onClick={onMore}>Show more</Button></div> : undefined}>
      <DataTableHead columns={[{ label: 'Question' }, { label: 'Who' }, { label: 'Organization' }, { label: 'When' }, { label: 'Misses' }]} />
      <tbody>
        {sessions.map((session) => (
          <DataTableRow key={session.id} to={coworkSessionPath(session.id)}>
            <DataTableCell><Link to={coworkSessionPath(session.id)}>{session.title || 'Untitled'}</Link></DataTableCell>
            <DataTableCell>{session.owner}</DataTableCell>
            <DataTableCell>{session.organizationName}</DataTableCell>
            <DataTableCell>{formatDateTime(session.updatedAt)}</DataTableCell>
            <DataTableCell>{session.misses}</DataTableCell>
          </DataTableRow>
        ))}
      </tbody>
    </DataTable>
  );
}

export function CoworkPage() {
  const [organizationId, setOrganizationId] = useState('');
  const [status, setStatus] = useState<CoworkMissStatus | ''>('new');
  const [misses, setMisses] = useState<CoworkMissesResponse | undefined>();
  const [sessions, setSessions] = useState<CoworkSessionRow[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(undefined);
    try {
      const filter = organizationId ? { organizationId } : {};
      const [missList, sessionList] = await Promise.all([
        adminClient.listCoworkMisses({ ...filter, ...(status ? { status } : {}) }),
        adminClient.listCoworkSessions(filter),
      ]);
      setMisses(missList);
      setSessions(sessionList.sessions);
      setNextBefore(sessionList.nextBefore);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not load Cowork');
    } finally {
      setLoading(false);
    }
  }, [organizationId, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const more = async () => {
    if (!nextBefore) return;
    const page = await adminClient.listCoworkSessions({ ...(organizationId ? { organizationId } : {}), before: nextBefore });
    setSessions((current) => [...current, ...page.sessions]);
    setNextBefore(page.nextBefore);
  };

  const changeStatus = async (miss: CoworkMiss, next: CoworkMissStatus) => {
    await adminClient.setCoworkMissStatus(miss.id, next);
    await load();
  };

  const names = new Map((misses?.organizations ?? []).map((organization) => [organization.id, organization.name]));
  const total = misses?.groups.reduce((sum, group) => sum + group.count, 0) ?? 0;

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader
        title="Cowork"
        description="What people asked, and what they wanted and did not get. Opening this page and reading a session are recorded."
        actions={
          <Button type="button" variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            <RotateCw className={cn('mr-1.5 h-3.5 w-3.5', loading && 'animate-spin')} aria-hidden="true" />
            Refresh
          </Button>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <select aria-label="Organization" className={FIELD_CLASS} value={organizationId} onChange={(event) => setOrganizationId(event.target.value)}>
          <option value="">All organizations</option>
          {(misses?.organizations ?? []).map((organization) => (
            <option key={organization.id} value={organization.id}>{organization.name}</option>
          ))}
        </select>
        <select aria-label="Status filter" className={FIELD_CLASS} value={status} onChange={(event) => setStatus(event.target.value as CoworkMissStatus | '')}>
          <option value="">Any status</option>
          {STATUSES.map((value) => (
            <option key={value} value={value}>{value}</option>
          ))}
        </select>
      </div>
      {loadError ? <LoadError message={loadError} onRetry={() => void load()} /> : null}
      {loading && !misses ? <SkeletonList rows={6} label="Loading Cowork" /> : null}
      {misses ? (
        <div className="flex flex-col gap-6">
          {total === 0 ? <p className="text-sm text-muted-foreground">Nothing here for these filters.</p> : null}
          {misses.groups.filter((group) => group.count > 0).map((group) => (
            <AdminSection key={group.kind} title={`${KIND_LABELS[group.kind]} (${group.count})`}>
              <ul>
                {group.misses.map((miss) => (
                  <MissRow key={miss.id} miss={miss} organizationName={names.get(miss.organizationId) ?? miss.organizationId} onStatus={(next) => void changeStatus(miss, next)} />
                ))}
              </ul>
            </AdminSection>
          ))}
          <AdminSection title="Sessions">
            {sessions.length ? <Sessions sessions={sessions} more={Boolean(nextBefore)} onMore={() => void more()} /> : <p className="text-sm text-muted-foreground">No sessions yet.</p>}
          </AdminSection>
        </div>
      ) : null}
    </div>
  );
}
